import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  Search,
  Filter,
  Calendar,
  Globe,
  User,
  Code,
  ArrowRight,
  Rocket,
  CheckCircle2,
  Clock,
  TrendingUp,
  Eye,
  ExternalLink,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { cn } from "../../lib/utils";
import { ProjectData } from "./ProjectData";

const Project = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [hoveredProject, setHoveredProject] = useState(null);

  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
      offset: 100,
    });
  }, []);

  // Get unique categories
  const categories = ["All", ...new Set(ProjectData.map((project) => project.category))];
  const statuses = ["All", "Completed", "Upcoming"];

  // Filter projects
  const filteredProjects = ProjectData.filter((project) => {
    const matchesSearch =
      project.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory =
      selectedCategory === "All" || project.category === selectedCategory;
    const matchesStatus =
      selectedStatus === "All" ||
      (selectedStatus === "Completed" && project.status === "completed") ||
      (selectedStatus === "Upcoming" && project.status === "upcoming");
    return matchesSearch && matchesCategory && matchesStatus;
  });

  const completedProjects = ProjectData.filter((p) => p.status === "completed");
  const upcomingProjects = ProjectData.filter((p) => p.status === "upcoming");

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 lg:py-32 bg-gradient-to-br from-background via-primary/5 to-background border-b overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05]">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, hsl(var(--foreground)) 1px, transparent 0)`,
              backgroundSize: "50px 50px",
            }}
          />
        </div>

        {/* Animated Gradient Orbs */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-3xl opacity-30 animate-pulse" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-primary/20 rounded-full blur-3xl opacity-30 animate-pulse delay-1000" />

        <div className="container relative z-10">
          <div className="text-center max-w-4xl mx-auto space-y-8">
            <div className="inline-block" data-aos="fade-down" data-aos-delay="100">
              <span className="text-sm font-medium text-primary bg-primary/10 px-4 py-2 rounded-full border border-primary/20">
                Our Portfolio
              </span>
            </div>

            <h1
              className="text-4xl md:text-5xl lg:text-6xl font-bold"
              data-aos="fade-up"
              data-aos-delay="200"
            >
              Our{" "}
              <span className="text-primary bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                Projects
          </span>
            </h1>

            <p
              className="text-lg md:text-xl text-muted-foreground leading-relaxed"
              data-aos="fade-up"
              data-aos-delay="300"
            >
              Explore our portfolio of successful projects and innovative solutions. From
              enterprise applications to cutting-edge technologies, we deliver excellence in every
              project.
            </p>

            {/* Search Bar */}
            <div
              className="max-w-2xl mx-auto"
              data-aos="fade-up"
              data-aos-delay="400"
            >
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search projects..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 h-14 text-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-muted/30 border-b">
        <div className="container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center" data-aos="fade-up" data-aos-delay="100">
              <div className="text-3xl md:text-4xl font-bold text-primary mb-2">
                {completedProjects.length}
              </div>
              <div className="text-sm text-muted-foreground">Completed Projects</div>
            </div>
            <div className="text-center" data-aos="fade-up" data-aos-delay="200">
              <div className="text-3xl md:text-4xl font-bold text-primary mb-2">
                {upcomingProjects.length}
              </div>
              <div className="text-sm text-muted-foreground">Upcoming Projects</div>
            </div>
            <div className="text-center" data-aos="fade-up" data-aos-delay="300">
              <div className="text-3xl md:text-4xl font-bold text-primary mb-2">50+</div>
              <div className="text-sm text-muted-foreground">Technologies Used</div>
            </div>
            <div className="text-center" data-aos="fade-up" data-aos-delay="400">
              <div className="text-3xl md:text-4xl font-bold text-primary mb-2">98%</div>
              <div className="text-sm text-muted-foreground">Client Satisfaction</div>
            </div>
          </div>
        </div>
      </section>

      {/* Filters Section */}
      <section className="py-8 bg-background border-b sticky top-0 z-10 backdrop-blur-sm">
        <div className="container">
          <div className="flex flex-wrap items-center gap-4 justify-between">
            <div className="flex items-center gap-2">
              <Filter className="h-5 w-5 text-muted-foreground" />
              <span className="text-sm font-medium">Filters:</span>
            </div>
            <div className="flex flex-wrap gap-3">
              {/* Category Filter */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Category:</span>
                <div className="flex gap-2">
                  {categories.slice(0, 6).map((category) => (
                    <button
                      key={category}
                      onClick={() => setSelectedCategory(category)}
                      className={cn(
                        "text-sm px-3 py-1 rounded-full transition-all",
                        selectedCategory === category
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted hover:bg-muted/80 text-muted-foreground"
                      )}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>
              {/* Status Filter */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Status:</span>
                <div className="flex gap-2">
                  {statuses.map((status) => (
                    <button
                      key={status}
                      onClick={() => setSelectedStatus(status)}
                      className={cn(
                        "text-sm px-3 py-1 rounded-full transition-all flex items-center gap-1",
                        selectedStatus === status
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted hover:bg-muted/80 text-muted-foreground"
                      )}
                    >
                      {status === "Completed" && <CheckCircle2 className="h-3 w-3" />}
                      {status === "Upcoming" && <Clock className="h-3 w-3" />}
                      {status}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-16 md:py-20 bg-background">
        <div className="container">
          {filteredProjects.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground text-lg">No projects found.</p>
      </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProjects.map((project, index) => (
                <Card
            key={project.id}
                  data-aos="fade-up"
                  data-aos-delay={100 + (index % 6) * 100}
                  className="border-border hover:border-primary/50 transition-all duration-300 hover:shadow-xl group overflow-hidden flex flex-col"
                  onMouseEnter={() => setHoveredProject(project.id)}
                  onMouseLeave={() => setHoveredProject(null)}
                >
                  <div className="relative aspect-video overflow-hidden">
            <img
              src={project.bannerImage}
              alt={project.title}
                      className={cn(
                        "w-full h-full object-cover transition-transform duration-500",
                        hoveredProject === project.id && "scale-110"
                      )}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <div className="absolute top-4 left-4 flex gap-2 flex-wrap">
                      <span
                        className={cn(
                          "text-xs font-medium px-3 py-1 rounded-full backdrop-blur-sm",
                          project.status === "completed"
                            ? "bg-green-500/90 text-white"
                            : "bg-yellow-500/90 text-white"
                        )}
                      >
                        {project.status === "completed" ? (
                          <span className="flex items-center gap-1">
                            <CheckCircle2 className="h-3 w-3" />
                            Completed
                          </span>
                        ) : (
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            Upcoming
                          </span>
                        )}
                      </span>
                      <span className="text-xs font-medium text-primary-foreground bg-primary/90 backdrop-blur-sm px-3 py-1 rounded-full">
                        {project.category}
                      </span>
                    </div>
                    <div
                      className={cn(
                        "absolute bottom-0 left-0 right-0 p-4 transition-all duration-300",
                        hoveredProject === project.id
                          ? "translate-y-0 opacity-100"
                          : "translate-y-4 opacity-0"
                      )}
                    >
                      <div className="flex flex-wrap gap-2">
                        {project.technologies.slice(0, 3).map((tech, idx) => (
                          <span
                            key={idx}
                            className="text-xs bg-background/90 backdrop-blur-sm text-foreground px-2 py-1 rounded-full"
                          >
                            {tech}
                          </span>
                        ))}
                        {project.technologies.length > 3 && (
                          <span className="text-xs bg-background/90 backdrop-blur-sm text-foreground px-2 py-1 rounded-full">
                            +{project.technologies.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <CardHeader>
                    <CardTitle className="text-xl mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                      {project.title}
                    </CardTitle>
                    <CardDescription className="line-clamp-2">
                      {project.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="flex-1 flex flex-col justify-between">
                    <div className="space-y-4">
                      {/* Project Stats */}
                      {project.stats && (
                        <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                          {project.stats.slice(0, 2).map((stat, idx) => (
                            <div key={idx} className="text-center">
                              <div className="text-lg font-bold text-primary">{stat.value}</div>
                              <div className="text-xs text-muted-foreground">{stat.label}</div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Technologies */}
                      <div className="flex flex-wrap gap-2">
                        {project.technologies.slice(0, 4).map((tech, idx) => (
                          <span
                            key={idx}
                            className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded-full"
                          >
                            {tech}
                          </span>
                        ))}
                      </div>

                      {/* Project Info */}
                      <div className="flex items-center gap-4 text-sm text-muted-foreground pt-2 border-t">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4" />
                          <span>{project.info.date}</span>
                        </div>
                        {project.info.location && (
                          <div className="flex items-center gap-2">
                            <Globe className="h-4 w-4" />
                            <span>{project.info.location}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <Button
                      asChild
                      variant="outline"
                      className="w-full mt-4 group/btn"
                    >
                      <Link to={`/projects/${project.id}`}>
                        {project.status === "completed" ? "View Project" : "Learn More"}
                        <ArrowRight className="ml-2 h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-primary/10 via-primary/5 to-background border-t">
        <div className="container">
          <Card
            className="border-border shadow-2xl overflow-hidden"
            data-aos="zoom-in"
            data-aos-delay="100"
          >
            <div className="bg-gradient-to-br from-primary/20 to-primary/5 p-12 md:p-16">
              <div className="text-center max-w-3xl mx-auto space-y-6">
                <Rocket className="h-12 w-12 text-primary mx-auto" />
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold">
                  Ready to Start Your Project?
                </h2>
                <p className="text-lg text-muted-foreground">
                  Let's discuss how we can bring your vision to life with our expertise and
                  innovative solutions.
                </p>
                <div className="flex flex-wrap justify-center gap-4 pt-4">
                  <Button size="lg" className="group" asChild>
                    <Link to="/contactUs">
                      Get In Touch
                      <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </Button>
                  <Button size="lg" variant="outline" asChild>
                    <Link to="/services">Our Services</Link>
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>
      </div>
  );
};

export default Project;
